import React, {createContext, useEffect, useState, useCallback} from 'react';
// const cc_ws_client = require('/Users/nitu/Circlechess/cc-client-sdk/lib/index.js');
// const cc_ws_client = require('/var/www/game-service/cc-client-sdk/lib/index.js');
import Environment from '../../environment';
import {useNotificationStore} from '@/Store/useNotificationStore';
import {useHeaderExtensionStore} from '@/Store/useHeaderExtensionStore';
import useGamePlayState from '@/Store/GamePlayStore';
import {useNavigation} from '@react-navigation/native';
import {getDeviceId, newSessionId} from '@/Utils/clientIdentity';
import {installDecodeHardening} from '@/Hooks/FunChallengesSocket/hardenDecode';

export const SocketContext = createContext<any>(null);

interface ISocketProvider {
  children: React.ReactNode;
  username: string;
  accessToken: string;
  setChangeClient: React.Dispatch<React.SetStateAction<boolean>>;
  setAuthComplete: React.Dispatch<React.SetStateAction<boolean>>;
}
// const schema = require('../../../../cc-client-sdk/lib/schema.js');
const {schema, cc_ws_client} = require('../../CCClientSdk');
//
export const SocketProvider = (props: ISocketProvider) => {
  const [gameClient, setGameClient] = useState<any>(null);
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const {children, username, accessToken, setChangeClient, setAuthComplete} =
    props;
  const navigation = useNavigation<any>();

  // Access the Zustand store
  const addNotification = useNotificationStore(state => state.addNotification);
  const addInboxMessage = useNotificationStore(state => state.addInboxMessage);
  const addUserRaisedIssue = useNotificationStore(
    state => state.addUserRaisedIssue,
  );
  const updateGameReviewNotification = useNotificationStore(
    state => state.updateGameReviewNotification,
  );
  const setRefetchStudy = useNotificationStore(state => state.setRefetchStudy);
  const setHeaderExtension = useHeaderExtensionStore(
    state => state.setHeaderExtension,
  );
  const clearHeaderExtension = useHeaderExtensionStore(
    state => state.clearHeaderExtension,
  );

  const parseSocketMessage = (raw: string) => {
    try {
      // First parse removes outer quotes
      const first = JSON.parse(raw);

      // Second parse gives actual object
      return typeof first === 'string' ? JSON.parse(first) : first;
    } catch (e) {
      console.error('Failed to parse inbox message', e);
      return null;
    }
  };

  const reconnect = useCallback(() => {
    setAuthComplete(false);
    setChangeClient(false);
    setReconnectNonce(prev => prev + 1);
  }, [setAuthComplete, setChangeClient]);

  useEffect(() => {
    if (!username || !accessToken) {
      setGameClient(null);
      return;
    }

    // Fresh per-connection id, constant for the life of this socket so the
    // server treats socket.io reconnects as a re-attach, not a new client.
    const sessionId = newSessionId();
    const socket = cc_ws_client(Environment.GAME_SERVICE_URL, {
      transports: ['websocket'],
      forceNew: true,
      query: {
        username: username,
        deviceId: getDeviceId(),
        sessionId: sessionId,
        Authorization: accessToken,
      },
    });

    installDecodeHardening(socket);

    setGameClient(socket);

    return () => {
      try {
        socket?.disconnect?.();
        socket?.off?.();
      } catch (error) {
        console.warn('Socket cleanup failed', error);
      }
    };
  }, [username, accessToken, reconnectNonce]);

  const notificationCallBack = useCallback(
    (socket: any, data: any) => {
      addNotification({
        requestId: data?.requestId || Date.now().toString(),
        id: data?.requestId || Date.now().toString(),
        message: data?.message,
        userId: data?.userId,
        type: data?.type || 'info',
        timestamp: new Date().toISOString(),
        isNew: true,
      });
    },
    [addNotification],
  );

  const inboxMessageCallBack = useCallback(
    (socket: any, data: any) => {
      console.log('inbox message data :', data);
      const parsed = parseSocketMessage(data?.message);
      if (!parsed) return;

      const channelId = parsed.channel_id;
      const messageId = parsed.message_id?.toString() ?? Date.now().toString();
      const clubId = Number(parsed.clubId ?? parsed.club_id);

      addInboxMessage({
        id: messageId,

        // 🔑 requestId now includes channelId
        requestId: channelId,

        message: parsed.body || '(No message)',
        userId: parsed.sender_id?.toString() ?? 'Unknown',
        type: 'inbox',
        senderName: parsed.sender_username || undefined,
        clubId: parsed.channel_type === 'CLUB' && Number.isFinite(clubId)
          ? clubId
          : undefined,

        timestamp: parsed.created_at || new Date().toISOString(),
        isNew: true,
      });
    },
    [addInboxMessage],
  );
  const userRaisedIssueCallBack = useCallback(
    (socket: any, data: any) => {
      console.log('ticket data :', data);
      const parsed = parseSocketMessage(data?.message);
      if (!parsed) return;

      const issueId = parsed.issue_id?.toString();
      if (!issueId) return;

      addUserRaisedIssue({
        id: issueId,
        requestId: `issue_${issueId}`,
        message: parsed.message || '(No message)',
        userId: parsed.user_id?.toString() || 'Unknown',
        type: 'support',
        timestamp: new Date().toISOString(),
        isNew: true,
      });
    },
    [addUserRaisedIssue],
  );
  const changeClient = useCallback(
    (socket: any, data: any) => {
      console.info(data, 'changeClient data');
      // Server follows up swapActive's MOVE_CLIENT with a sentinel after an
      // accept-from-non-active promote, so demoted tabs don't keep showing
      // "Activate here" while the user is now playing on another tab.
      if (data?.message === 'playing_elsewhere') {
        setChangeClient(false);
      } else {
        setChangeClient(true);
      }
    },
    [setChangeClient],
  );

  function parseNestedJsonString(str) {
    try {
      // First JSON.parse removes the extra quoting/escaping
      const innerJsonString = JSON.parse(str);
      // Second JSON.parse turns the real JSON text into an object
      return JSON.parse(innerJsonString);
    } catch (err) {
      console.error('Failed to parse nested JSON string:', err);
      return null;
    }
  }

  const gameAnalysisNotificationCallBack = useCallback(
    (socket: any, data: any) => {
      const notification = {
        requestId: data?.requestId || Date.now().toString(),
        id: data?.requestId || Date.now().toString(),
        message: parseNestedJsonString(data?.message),
        userId: data?.user_id,
        source: data?.source,
        type: data?.type || 'game_review',
        timestamp: new Date().toISOString(),
        isNew: true,
      };
      console.log('gameAnalysisNotificationCallBack data', notification);

      updateGameReviewNotification(notification);
      //   // Optionally, also add to main notifications:
      //   // useNotificationStore.getState().addNotification(notification);
      // },
    },
    [updateGameReviewNotification],
  );
  const headerExtensionCallBack = useCallback(
    (socket: any, data: any) => {
      console.log('header_status_message data:', data);
      const parsed = parseNestedJsonString(data?.message);
      if (!parsed) return;

      const statusMessage = parsed?.status_message;
      if (statusMessage) {
        setHeaderExtension({
          icon: statusMessage.icon,
          main_text: statusMessage.main_text,
          sub_text: statusMessage.sub_text,
          cta_details: statusMessage.cta_details,
          close_icon: statusMessage.close_icon,
        });
      } else {
        clearHeaderExtension();
      }
    },
    [setHeaderExtension],
  );

  const setJoinedTournamentId = useGamePlayState(
    state => state.setJoinedTournamentId,
  );
  const tournamentNavigation = useCallback(
    (socket: any, data: any) => {
      console.info(data, 'navigate to PlayChess data');

      if (
        data?.type === 'join_tournament_response' &&
        data?.playerId === username &&
        data?.tournamentId
      ) {
        setJoinedTournamentId(String(data.tournamentId));
      }

      // Skip the redirect when the user is already on a screen that mounts
      // PlayChess. Both the playChess route and the CCTournaments route
      // render the same PlayChess component (Tournaments screen was added
      // later and is just a wrapper around PlayChess) — navigating from
      // CCTournaments to playChess remounts the component and loses the
      // in-flight tournament context.
      const path = (window?.location?.pathname || '').toLowerCase();
      if (
        path.includes('/classroom') ||
        path.includes('/playchess') ||
        path.includes('/cctournaments')
      ) {
        console.log(
          `User is on ${path} (PlayChess already mounted), not navigating`,
        );
        return;
      }
      if (
        data.type === 'join_tournament_response' &&
        data.playerId !== username
      ) {
        console.info('Ignoring other player join tournament response');
        return;
      }
      navigation.navigate('PlayChess');
    },
    [navigation, setJoinedTournamentId, username],
  );

  useEffect(() => {
    if (gameClient) {
      gameClient.registerMessageType(
        'notification',
        schema.notificationMsgSchema,
      );
      gameClient.registerMessageType(
        'inbox_message',
        schema.notificationMsgSchema,
      );
      gameClient.registerMessageType(
        'user_raised_issue_message',
        schema.notificationMsgSchema,
      );
      gameClient.registerMessageHandler(
        'notification',
        function (socket: any, data: any) {
          notificationCallBack(socket, data);
        },
        'SocketProvider:notification',
      );
      gameClient.registerMessageHandler(
        'inbox_message',
        function (socket: any, data: any) {
          inboxMessageCallBack(socket, data);
        },
        'SocketProvider:inbox_message',
      );
      gameClient.registerMessageHandler(
        'user_raised_issue_message',
        function (socket: any, data: any) {
          userRaisedIssueCallBack(socket, data);
        },
        'SocketProvider:user_raised_issue_message',
      );
      // gameClient.registerMessageType('game_analysis', schema.gameReviewSchema);
      gameClient.registerMessageHandler(
        'game_analysis',
        function (socket: any, data: any) {
          gameAnalysisNotificationCallBack(socket, data);
        },
        'SocketProvider:game_analysis',
      );
      gameClient.registerMessageHandler(
        'header-extension-state',
        function (socket: any, data: any) {
          headerExtensionCallBack(socket, data);
        },
        'SocketProvider:header-extension-state',
      );
      gameClient.registerMessageType(
        'alternate_move_update',
        schema.alternateStudyMove,
      );
      gameClient.registerMessageHandler(
        'alternate_move_update',
        function (_socket: any, _data: any) {
          setRefetchStudy(true);
        },
        'SocketProvider:alternate_move_update',
      );
      gameClient.registerMessageType(
        'change_client_connection',
        schema.changeClientResMsgSchema,
      );
      gameClient.registerMessageType(
        'fetch_game_state',
        schema.fetchGameStateMsgSchema,
      );
      gameClient.registerMessageHandler(
        'change_client_connection',
        function (socket: any, data: any) {
          changeClient(socket, data);
        },
        'SocketProvider:change_client_connection',
      );
      const handleAuthComplete = function (data: any) {
        if (data?.success || data?.user) {
          setChangeClient(false);
          setAuthComplete(true);
          console.log('auth complete event data', data);
          const packet: any = {
            requestId: new Date().getTime().toString(),
            type: 'fetch_game_state',
            userId: username,
          };

          console.info(
            'fetch game state packet is :',
            JSON.stringify(packet),
            gameClient.id,
          );

          gameClient.send_msg('fetch_game_state', packet);
        } else {
          setAuthComplete(false);
        }
      };

      const handleDisconnect = function () {
        setAuthComplete(false);
      };

      gameClient.on('auth_complete', handleAuthComplete);
      gameClient.on('disconnect', handleDisconnect);
      gameClient.on('close', handleDisconnect);

      gameClient.registerMessageType(
        'pairing',
        schema.tournamentPairingBroadcastSchema,
      );

      gameClient.registerMessageType(
        'join_tournament_response',
        schema.joinTournamentResMsgSchema,
      );

      if (tournamentNavigation) {
        gameClient.registerMessageHandler(
          'pairing',
          function (socket: any, data: any) {
            tournamentNavigation(socket, data);
          },
          'SocketProvider:pairing',
        );
        gameClient.registerMessageHandler(
          'join_tournament_response',
          function (socket: any, data: any) {
            tournamentNavigation(socket, data);
          },
          'SocketProvider:join_tournament_response',
        );
        gameClient.registerMessageHandler(
          'start_game_response',
          function (socket: any, data: any) {
            tournamentNavigation(socket, data);
          },
          'SocketProvider:start_game_response',
        );
      }

      // Register gameReview message type and handler
      // if (schema.gameReviewSchema) {
      //   gameClient.registerMessageType('gameReview', schema.gameReviewSchema);
      //   gameClient.registerMessageHandler(
      //     'gameReview',
      //     function (socket: any, data: any) {
      //       // Add to gameReviewNotifications (and optionally notifications)
      //       const notification = {
      //         requestId: data?.requestId || Date.now().toString(),
      //         id: data?.requestId || Date.now().toString(),
      //         message: data?.message?.replace(/"/g, ''),
      //         userId: data?.user_id,
      //         source: data?.source,
      //         type: data?.type || 'game_review',
      //         timestamp: new Date().toISOString(),
      //         isNew: true,
      //       };
      //       debugger;

      //       updateGameReviewNotification(notification);
      //       //   // Optionally, also add to main notifications:
      //       //   // useNotificationStore.getState().addNotification(notification);
      //       // },
      //     },
      //   );
      // }
      return () => {
        gameClient.off('auth_complete', handleAuthComplete);
        gameClient.off('disconnect', handleDisconnect);
        gameClient.off('close', handleDisconnect);
      };
    }
  }, [
    gameClient,
    changeClient,
    notificationCallBack,
    headerExtensionCallBack,
    setAuthComplete,
    setChangeClient,
    setRefetchStudy,
    tournamentNavigation,
    username,
  ]);

  return (
    <SocketContext.Provider value={{socket: gameClient, reconnect}}>
      {children}
    </SocketContext.Provider>
  );
};
